import React, { useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Progress from 'core/components/progress/Progress'
import { durationBetweenDates, formatDate } from 'utils/misc'
import SimpleLink from 'core/components/SimpleLink'
import { routeManager } from 'core/utils/routes'
import { humanReadableSize } from 'app/plugins/openstack/helpers'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import { listImages } from '../actions'
import { listVirtualMachinesLite } from 'openstack/components/vms/actions'
import { listVolumeSnapshots } from 'openstack/components/storage/snapshots/actions'
import { virtualMachinesLiteByIdSelector } from 'openstack/components/vms/selectors'
import { volumeSnapshotsByIdSelector } from 'openstack/components/storage/snapshots/selectors'
import { ImageDetailsPageTabs } from './model'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import { IVolumeSnapshotDetailsPageTabs } from 'openstack/components/storage/snapshots/details/model'
import OverviewCard from 'openstack/components/vms/vm-details/OverviewCard'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  overview: {
    display: 'grid',
    gridTemplateColumns: '370px 1fr',
    marginTop: '16px',
    gridGap: '24px',
  },
  column: {
    display: 'grid',
    gridGap: '24px',
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
  },
}))

const tpmModelText = {
  'tpm-crb': 'CRB',
  'tpm-tis': 'TIS',
}

export default function Overview({ image, loading }) {
  const classes = useStyles({})

  const { loading: loadingVms } = useListAction(listVirtualMachinesLite)
  const vmsById = useSelector(virtualMachinesLiteByIdSelector)

  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector)
  console.log(image, 'image')

  const overviewData = useMemo(
    () =>
      image
        ? [
            {
              key: 'Name',
              value: image?.name,
            },
            {
              key: 'ID',
              value: image?.id,
            },
            {
              key: 'Status',
              value: image?.status,
            },
            {
              key: 'Type',
              value: image?.imageTypeDisplay,
            },
            {
              key: 'Source VM',
              value: image?.instance_uuid,
              condition: ({ instance_uuid }) => !!instance_uuid,
              render: ({ instance_uuid }) =>
                instance_uuid ? (
                  <SimpleLink
                    src={routeManager.routes.openstack.vmDetails.path({
                      id: instance_uuid,
                      tab: IVmDetailsPageTabs.Overview,
                    })}
                  >
                    {vmsById?.[instance_uuid]?.name || instance_uuid}
                  </SimpleLink>
                ) : null,
            },
            {
              key: 'Volume Snapshots',
              value: image?.snapshotIds,
              condition: ({ snapshotIds }) => snapshotIds && snapshotIds.length,
              render: ({ snapshotIds }) =>
                snapshotIds.length ? (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {snapshotIds?.map((snapshotId) => (
                      <div key={snapshotId}>
                        <SimpleLink
                          src={routeManager.routes.openstack.volumeSnapshotDetails.path({
                            id: snapshotId,
                            tab: IVolumeSnapshotDetailsPageTabs.Overview,
                          })}
                        >
                          {volumeSnapshotsById?.[snapshotId]?.name || snapshotId}
                        </SimpleLink>
                      </div>
                    ))}
                  </div>
                ) : null,
            },
            {
              key: 'Visibility',
              value: image?.visibility,
            },
            {
              key: 'Protected',
              value: image?.protected,
              render: (val) => (val?.protected ? 'True' : 'False'),
            },
            {
              label: 'Disk Format',
              key: image?.disk_format,
            },
            {
              key: 'Virtual Disk Size',
              value: image?.virtual_size,
              render: ({ virtual_size }) => humanReadableSize(virtual_size),
            },
            {
              key: 'File Size',
              value: image?.size,
              render: ({ size }) => humanReadableSize(size),
            },
            {
              key: 'Virtual TPM 2.0',
              value: image?.hw_tpm_model,
              render: (val) =>
                val?.hw_tpm_version === '2.0' ? `Enabled (${tpmModelText[val?.hw_tpm_model]})` : '',
            },
            {
              key: 'Age',
              value: image?.created_at,
              render: ({ created_at }) => durationBetweenDates({ labels: ['d', 'h'] })(created_at),
            },
          ]
        : [],
    [vmsById, volumeSnapshotsById],
  )

  return (
    <Progress loading={loading}>
      <div className={classes.overview}>
        <div className={classes.column}>
          <OverviewCard item={image} data={overviewData} icon="desktop" />
        </div>
      </div>
    </Progress>
  )
}
